import {Analysis} from "./analysis";
import {AnalysisStatusEnum} from "./analysis-status";
import {Product} from "./product";
import {Scenario} from "./scenario";
import {Step} from "./step";
import {Vehicle} from "./vehicle";
import {ONE_WEEK_MS, PictureKind, StoredPicture, UploadState, isOlderThan} from "./stored-picture";

export enum SyncAction {
  NONE = 'NONE',
  PURGE = 'PURGE',
  RESUME = 'RESUME',
  REUSE = 'REUSE',
  ABORT_IDENTIFICATION = 'ABORT_IDENTIFICATION'
}

export interface SyncContext {
  analysis: Analysis;
  vehicle?: Vehicle;
  scenario: Scenario;
  product: Product;
  pictures: StoredPicture[];
  now?: number;
}

export interface SyncResult {
  action: SyncAction;
  confirmedSteps: string[];
}

export const confirmedServerSteps = (analysis: Analysis): string[] => {
  const confirmed = (tyres: Analysis['referenceTyres'], type: string) => tyres
    .filter(tyre => !!tyre.position && tyre.status !== AnalysisStatusEnum.STARTED)
    .map(tyre => `${tyre.position}_${type}`);
  return [
    ...confirmed(analysis.referenceTyres, 'REFERENCE'),
    ...confirmed(analysis.wearTyres, 'WEAR')
  ];
}

export const isConfirmed = (picture: StoredPicture, confirmedSteps: string[]): boolean => {
  return picture.kind === PictureKind.TYRE
    ? confirmedSteps.includes(picture.key)
    : picture.uploadState === UploadState.UPLOADED;
}

export const uploadedLocalSteps = (pictures: StoredPicture[], analysisId?: number): string[] => {
  return pictures
    .filter(picture => picture.kind === PictureKind.TYRE
      && picture.analysisId === analysisId
      && picture.uploadState === UploadState.UPLOADED)
    .map(picture => picture.key);
}

export const coversScenario = (confirmedSteps: string[], scenario: Scenario): boolean => {
  const confirmed = new Set(confirmedSteps);
  return scenario.steps.length > 0
    && scenario.steps.every((step: Step) => confirmed.has(step.toString()));
}

/**
 * POST /v1/web-app/analyses identifies a vehicle by its license only. Without one the back end
 * cannot find the vehicle again and creates a new one, so a restored analysis whose vehicle has
 * no license is unreachable and the identification has to be done again.
 */
export const canResumeAnalysis = (vehicle?: Vehicle): boolean => !!vehicle?.license?.license;

// Use stage when back will have finish his dev
const isTerminal = (analysis: Analysis): boolean => {
  return analysis.status === AnalysisStatusEnum.UPLOADED
    || analysis.status === AnalysisStatusEnum.AI_READY
    || analysis.status === AnalysisStatusEnum.FINISHED
    || analysis.status === AnalysisStatusEnum.ABORTED;
}

/** True when both sides carry the identifier and it is the same, or when it cannot be compared. */
const isCompatible = (stored?: string | number, current?: string | number): boolean =>
  !stored || !current || stored === current;

/**
 * A picture is kept only when it can be proven to belong to the current vehicle: at least one
 * identifier both sides carry, and no identifier that contradicts.
 */
const belongsToVehicle = (picture: StoredPicture, vehicle?: Vehicle): boolean => {
  const identified = !!(picture.vehicleId && vehicle?.id) || !!(picture.license && vehicle?.license?.license);

  return identified
    && isCompatible(picture.vehicleId, vehicle?.id)
    && isCompatible(picture.license, vehicle?.license?.license);
}

const hasUnconfirmedIdentification = (pictures: StoredPicture[]): boolean => {
  return pictures.some(picture => picture.kind !== PictureKind.TYRE && picture.uploadState !== UploadState.UPLOADED);
}

export const decideSync = (context: SyncContext): SyncAction => {
  const {analysis, vehicle, scenario, product, pictures} = context;
  const now = context.now ?? Date.now();

  if (!pictures.length) {
    return SyncAction.NONE;
  }

  if (isTerminal(analysis) || !analysis.id) {
    return SyncAction.PURGE;
  }

  if (!pictures.every(picture => belongsToVehicle(picture, vehicle))) {
    return SyncAction.PURGE;
  }

  const sameAnalysis = pictures.every(picture => picture.analysisId === analysis.id);

  if (sameAnalysis) {
    return hasUnconfirmedIdentification(pictures)
      ? SyncAction.ABORT_IDENTIFICATION
      : SyncAction.RESUME;
  }

  const tyres = pictures.filter(picture => picture.kind === PictureKind.TYRE);
  if (tyres.length !== scenario.steps.length) {
    return SyncAction.PURGE;
  }

  if (pictures.some(picture => isOlderThan(picture, ONE_WEEK_MS, now))) {
    return SyncAction.PURGE;
  }

  return product === Product.SCANNER ? SyncAction.PURGE : SyncAction.REUSE;
}
